import React, { memo } from 'react';
import cn from 'classnames';

import { useRouter } from 'next/router';
import { IconButton } from 'components';
import { Button } from 'components/form';
import { Row } from 'components/layout';
import { ButtonV3 } from 'components/ComponentV2';
import { PAGE_ROUTES } from 'lib/page-routes';
import { TouchpointType } from 'lib/models/touchpoint';
import styles from './Header.module.scss';

export interface HeaderProps {
  /** Custom class name */
  className?: string;
  /** Custom style */
  style?: Record<string, unknown>;
  step: number;
  onClose: () => void;
  onNavigate: (goToStep: number) => void;
  onNext: () => void;
  onNextLoading?: boolean;
  onPreviewClick?: () => void;
  primaryButtonText?: string;
}

export const Header: React.FC<HeaderProps> = memo(
  ({
    className = '', // custom class name
    style, // custom style
    step,
    onClose,
    onNavigate,
    onNext,
    onNextLoading,
    onPreviewClick,
    primaryButtonText,
  }: HeaderProps) => {
    const contentClassNames = cn(
      {
        [styles.Header]: true,
      },
      className
    );

    const router = useRouter();
    const { type = 'Job' } = router.query;
    const isEdit =
      router.pathname === PAGE_ROUTES.CAMPAIGNS_EDIT ||
      router.pathname === PAGE_ROUTES.EVENTS_EDIT ||
      router.pathname === PAGE_ROUTES.HIRING_EDIT;

    return (
      <div className={contentClassNames} style={style} data-testid="Header">
        <Row align="center" className={styles.row1}>
          <IconButton
            iconName="close"
            size="auto"
            onClick={onClose}
            className={styles.close}
          />
          <p className={styles.text}>
            {isEdit ? `Edit ${type}` : `New ${type}`}
          </p>
        </Row>
        <Row align="center" justify="center" className={styles.row2}>
          <Button
            onClick={() => onNavigate(1)}
            variant="text"
            className={cn(styles.button, {
              [styles.buttonSelected]: step === 1,
            })}
          >
            <span
              className={cn(styles.number, {
                [styles.numberSelected]: step === 1,
              })}
            >
              1
            </span>
            Basic info
          </Button>
          <div className={styles.separator}></div>
          <Button
            onClick={() => onNavigate(2)}
            variant="text"
            className={cn(styles.button, {
              [styles.buttonSelected]: step === 2,
            })}
          >
            <span
              className={cn(styles.number, {
                [styles.numberSelected]: step === 2,
              })}
            >
              2
            </span>
            Details
          </Button>
          <div className={styles.separator}></div>
          <Button
            onClick={() => onNavigate(3)}
            variant="text"
            className={cn(styles.button, {
              [styles.buttonSelected]: step === 3,
            })}
          >
            <span
              className={cn(styles.number, {
                [styles.numberSelected]: step === 3,
              })}
            >
              3
            </span>
            {type === TouchpointType.Event
              ? 'Registrations'
              : 'Application info'}
          </Button>
        </Row>
        {step === 3 ? (
          <Row className={styles.row3} justify="end">
            <ButtonV3 color="secondary" size="small" onClick={onPreviewClick}>
              Preview
            </ButtonV3>
            <ButtonV3 size="small" onClick={onNext} isLoading={onNextLoading}>
              {primaryButtonText || 'Submit for approval'}
            </ButtonV3>
          </Row>
        ) : (
          <Row className={styles.row3} justify="end">
            <ButtonV3
              size="small"
              onClick={onNext}
              isLoading={onNextLoading}
              className={styles.buttonNext}
            >
              Next
            </ButtonV3>
          </Row>
        )}
      </div>
    );
  }
);

Header.displayName = 'Header';
